import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { Routes } from '@angular/router';
import { adminGuard } from './guards/admin.guard';
import { deliveryGuard, deliveryGuestGuard } from './guards/delivery.guard';
import { sellerAuthInterceptor } from './guards/seller-auth.interceptor';
import { SellerOperationalGuard } from './guards/seller-operational.guard';
import { SellerGuard } from './guards/seller.guard';
import { SellarLoginComponent } from './Seller-Panel/sellar-login/sellar-login.component';

/**
 * Angular panels app — customer storefront lives in Next.js (`apps/storefront`).
 * Keep seller, admin, delivery, and seller-owned public site surfaces here.
 */
export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'sellar-login',
  },
  {
    path: 'login',
    pathMatch: 'full',
    redirectTo: 'sellar-login',
  },
  {
    path: 'seller-login',
    pathMatch: 'full',
    redirectTo: 'sellar-login',
  },

  // —— Admin ——
  {
    path: 'admin-login',
    loadComponent: () =>
      import('./Admin-Panel/admin-login/admin-login.component').then((m) => m.AdminLoginComponent),
  },
  {
    path: 'Admin-Panel/Dashboard',
    loadChildren: () =>
      import('./Admin-Panel/admin-dashboard.routes').then((m) => m.ADMIN_DASHBOARD_ROUTES),
    canActivate: [adminGuard],
  },

  // —— Seller auth / onboarding ——
  {
    path: 'sellar-signup',
    loadComponent: () =>
      import('./Seller-Panel/sellar-signup/sellar-signup.component').then(
        (m) => m.SellarSignupComponent,
      ),
  },
  {
    path: 'sellar-login',
    // Eager: this is the seller-host landing page. Lazy + SSR hydration left
    // jsaction placeholders on Continue and the form never became live.
    component: SellarLoginComponent,
  },
  {
    path: 'sellar-member-login',
    loadComponent: () =>
      import('./Seller-Panel/seller-child-login/seller-child-login.component').then(
        (m) => m.SellerChildLoginComponent,
      ),
  },
  {
    path: 'seller-child-change-password',
    loadComponent: () =>
      import('./Seller-Panel/child-change-password/child-change-password.component').then(
        (m) => m.ChildChangePasswordComponent,
      ),
  },
  {
    path: 'sellar-verification',
    loadComponent: () =>
      import(
        './Seller-Panel/dashboard-seller/components/seller-verification/seller-verification.component'
      ).then((m) => m.SellerVerificationComponent),
  },
  {
    path: 'verify-otp/:id',
    loadComponent: () =>
      import('./Seller-Panel/verify-otp/verify-otp.component').then((m) => m.VerifyOtpComponent),
  },
  {
    path: 'seller-update-password',
    loadComponent: () =>
      import('./Seller-Panel/change-password-seller/change-password-seller.component').then(
        (m) => m.ChangePasswordSellerComponent,
      ),
  },
  {
    path: 'seller-reset-password/:sellerId/:passwordResetToken',
    loadComponent: () =>
      import('./Seller-Panel/reset-seller-password/reset-seller-password.component').then(
        (m) => m.ResetSellerPasswordComponent,
      ),
  },
  {
    path: 'seller-reset-password/:sellerId/:childId/:token',
    loadComponent: () =>
      import('./Seller-Panel/reset-seller-password/reset-seller-password.component').then(
        (m) => m.ResetSellerPasswordComponent,
      ),
  },
  {
    path: 'seller-dashboard',
    loadComponent: () =>
      import('./Seller-Panel/dashboard-seller/dashboard-seller.component').then(
        (m) => m.DashboardSellerComponent,
      ),
    canActivate: [SellerGuard],
    canActivateChild: [SellerOperationalGuard],
    providers: [provideHttpClient(withInterceptors([sellerAuthInterceptor]))],
    loadChildren: () =>
      import('./Seller-Panel/seller-dashboard.routes').then((m) => m.SELLER_DASHBOARD_ROUTES),
  },

  // —— Seller-owned public surfaces (not customer shop) ——
  {
    path: 'store-preview/:websiteName',
    loadComponent: () =>
      import('./pages/store-preview/store-preview.component').then((m) => m.StorePreviewComponent),
  },
  {
    path: 'brand/:slug',
    loadComponent: () =>
      import('./pages/company-brand-site/company-brand-site.component').then(
        (m) => m.CompanyBrandSiteComponent,
      ),
  },
  {
    path: 'site/:slug',
    loadComponent: () =>
      import('./pages/ai-seller-site/ai-seller-site.component').then(
        (m) => m.AiSellerSiteComponent,
      ),
  },
  {
    path: 'passport/:id',
    loadComponent: () =>
      import('./pages/product-passport/product-passport.component').then(
        (m) => m.ProductPassportComponent,
      ),
  },

  // —— Delivery panel ——
  {
    path: 'delivery/login',
    loadComponent: () =>
      import('./pages/delivery/delivery-login.component').then((m) => m.DeliveryLoginComponent),
    canActivate: [deliveryGuestGuard],
  },
  {
    path: 'delivery/signup',
    loadComponent: () =>
      import('./pages/delivery/delivery-signup.component').then((m) => m.DeliverySignupComponent),
    canActivate: [deliveryGuestGuard],
  },
  {
    path: 'delivery/contact/:agentId',
    loadComponent: () =>
      import('./pages/delivery/delivery-contact.component').then((m) => m.DeliveryContactComponent),
  },
  {
    path: 'delivery/report',
    loadComponent: () =>
      import('./pages/delivery/delivery-report.component').then((m) => m.DeliveryReportComponent),
  },
  {
    path: 'delivery',
    loadComponent: () =>
      import('./pages/delivery/delivery-layout.component').then((m) => m.DeliveryLayoutComponent),
    canActivate: [deliveryGuard],
    loadChildren: () => import('./pages/delivery/delivery.routes').then((m) => m.DELIVERY_ROUTES),
  },

  {
    path: '**',
    loadComponent: () =>
      import('./pages/not-found/not-found.component').then((m) => m.NotFoundComponent),
    title: '404 - Page Not Found | MerchantsBazaar',
  },
];
